/**
 * Evidence identity and the evidence rules (pedagogy spec §2.4, canonical for
 * every WP; learning-evidence design §2.2a, decision §8 #1).
 *
 * - Identity: a test-bank variant family is one problem (its up to four
 *   numeric variants differ only in the numbers); the embedded items of one
 *   learning sequence share `seq:<id>`; every other question is its own
 *   problem. `shared/mastery.ts` groups attempts by this key before weighting
 *   them, and practice selection collapses families with the same id.
 * - "With help" (`annotateEvidence`), the calibration series (§3.3.3) and the
 *   classification behind Progress, Today and the sequence status
 *   (`classifyEvidence`): delayed retrievals, open misses, topic states, the
 *   due re-test list, recurring slips and "Coming back".
 *
 * Pure and deterministic, no I/O. The client imports this file too, so it
 * takes only types from `content/schema.ts` (no zod in the bundle).
 */
import type { SlipKind } from "../content/schema.js";
import type { ConfidenceLevel } from "./attemptAnswer.js";
import { addDays } from "./time.js";
import type { Question, QuestionType } from "./types.js";

/**
 * The test-bank family a question belongs to (`source.familyId`, e.g.
 * "T08-N08"), or `null` outside the test bank. Single-variant families also
 * return their id.
 */
export function familyIdOf(question: Pick<Question, "source">): string | null {
  return question.source.kind === "testbank" ? question.source.familyId : null;
}

/**
 * The evidence key: the family id for a test-bank item, otherwise the question
 * id. Family ids are upper case ("T08-N08") and question ids are lower-case
 * kebab case, so the two never collide today; `content/scripts/validate.ts`
 * enforces this (`familyIdCollisions`, `content/scripts/evidence-keys.ts`).
 * Embedded sequence items use `sequenceKeyOf` instead (the mapper decides).
 */
export function evidenceKeyOf(question: Pick<Question, "id" | "source">): string {
  return familyIdOf(question) ?? question.id;
}

/** Prefix of the key every embedded item of one learning sequence shares (§8.3.3). */
export const SEQUENCE_KEY_PREFIX = "seq:";

/** `seq:<sequenceId>`: the evidence key of a sequence's set tasks and completions. */
export function sequenceKeyOf(sequenceId: string): string {
  return `${SEQUENCE_KEY_PREFIX}${sequenceId}`;
}

/** Sequence keys are never delayed retrievals, open misses or re-test keys (§2.4). */
export function isSequenceKey(key: string): boolean {
  return key.startsWith(SEQUENCE_KEY_PREFIX);
}

/** A test-bank item's variant number (`source.variant`, 1…4); 1 outside the test bank. */
export function variantOf(question: Pick<Question, "source">): number {
  return question.source.kind === "testbank" ? question.source.variant : 1;
}

/**
 * True when `candidate` is the better representative of its family than
 * `current`: an unattempted variant beats an attempted one, an older attempt
 * beats a newer one, and — the tie every fresh student hits — the lower
 * variant number wins, so variant 1 is served unless it has been attempted.
 * Practice selection collapses families with it; Today picks the sibling it
 * serves as a re-test in the same order (§6.3.2).
 */
export function isBetterVariant(
  candidate: Question,
  current: Question,
  lastAttemptAt: ReadonlyMap<string, number>,
): boolean {
  const a = lastAttemptAt.get(candidate.id);
  const b = lastAttemptAt.get(current.id);
  if ((a === undefined) !== (b === undefined)) return a === undefined;
  if (a !== undefined && b !== undefined && a !== b) return a < b;
  return variantOf(candidate) < variantOf(current);
}

/* ------------------------ evidence attempts (§2.4) ------------------------ */

/**
 * Attempt kinds the evidence rules read: `retry` is the guided retry (step
 * 2.4), `plan` a "Which principle?" pick (step 2.8; a glimpse, never an
 * exposure).
 */
export type EvidenceKind = "answer" | "reveal" | "retry" | "plan";
export const EVIDENCE_KINDS: readonly EvidenceKind[] = ["answer", "reveal", "retry", "plan"];

/** Roles of rows inside a learning sequence (pedagogy spec §8.3.3); none outside one. */
export type SequenceRole = "task" | "completion" | "final" | "testout";

/** `answer_json.followUp.kind` of an inserted follow-up (§5.3.2). */
export type FollowUpKind = "contrast" | "similar";

/**
 * One attempt row as the evidence rules see it (pedagogy spec §2.4), built by
 * `server/src/content/masteryInput.ts#toEvidenceAttempts` from the projected
 * read: one per row whose question still resolves, ascending by `createdAt`.
 * Reveal and plan rows carry no score, so the score is nullable.
 */
export interface EvidenceAttempt {
  id: string;
  questionId: string;
  /** `familyId ?? questionId`, or `seq:<id>` for a sequence task or completion. */
  evidenceKey: string;
  /** Primary topic (`full.topic`); evidence never uses secondary topics. */
  topic: string;
  chapter: number;
  questionType: QuestionType;
  mode: "practice" | "exam";
  kind: EvidenceKind;
  gradedBy: "auto" | "self";
  correct: boolean | null;
  score: number | null;
  maxScore: number | null;
  createdAt: number;
  /** Local calendar day in the app time zone, computed once per row. */
  day: string;
  hintsUsed: number;
  confidence: ConfidenceLevel | null;
  /** `plan.correct`: the plan-first verdict on an answer, the pick's on a plan row. */
  planCorrect: boolean | null;
  followUpKind: FollowUpKind | null;
  /** `origin.retestOf`: the key a Today re-test was served for (§2.2). */
  retestOf: string | null;
  sequenceId: string | null;
  sequenceRole: SequenceRole | null;
  /** `sequence.reopened`: the worked examples were reopened before a final. */
  sequenceReopened: boolean;
  /** Stored slip kinds (`answer_json.slips`), unknown kinds dropped. */
  slips: readonly SlipKind[];
  /** An earlier exposure (answer, retry or reveal, any mode) of the same key on the same day. */
  sameDayRepeat: boolean;
  /** "With help" (§2.4, weight 0.5): see `isAssistedAnswer`. */
  assisted: boolean;
}

/** An evidence attempt before `annotateEvidence` adds the same-day flags. */
export type EvidenceAttemptInput = Omit<EvidenceAttempt, "sameDayRepeat" | "assisted">;

/**
 * The §2.4 "with help" classes (§12 C3: each weighs 0.5 in mastery): a retry;
 * or a practice answer after a hint, after an earlier exposure or plan glimpse
 * of its key that day (every `/answer` hands over the solution), with a wrong
 * plan-first pick, as a contrast follow-up (the twin follows the source's
 * worked solution), as a sequence completion, or as a final with the examples
 * reopened. Exam rows never are; reveal and plan rows are not answers. The
 * open "Klar → Spara" flow is unaided: it grades the locked pre-reveal text.
 */
export function isAssistedAnswer(
  row: Pick<
    EvidenceAttemptInput,
    | "kind"
    | "mode"
    | "hintsUsed"
    | "planCorrect"
    | "followUpKind"
    | "sequenceRole"
    | "sequenceReopened"
  >,
  earlierSameDay: boolean,
): boolean {
  if (row.mode === "exam") return false;
  if (row.kind === "retry") return true;
  if (row.kind !== "answer") return false;
  return (
    row.hintsUsed > 0 ||
    earlierSameDay ||
    row.planCorrect === false ||
    row.followUpKind === "contrast" ||
    row.sequenceRole === "completion" ||
    (row.sequenceRole === "final" && row.sequenceReopened)
  );
}

/** `createEvidenceAnnotator`'s per-(day, key) bits: an exposure, a plan glimpse. */
const EXPOSED = 1;
const GLIMPSED = 2;

/**
 * A stateful annotator for rows fed in ascending `createdAt` order: it sets
 * `sameDayRepeat` (an earlier exposure of the key that local day) and
 * `assisted` on the row it is given, in place (the mapper builds each row
 * once; copying 25 fields per row would double its cost), and returns it.
 * Plan rows are glimpses: they never make a later row a repeat, but a
 * same-day answer on the glimpsed key is assisted (the principle was named).
 */
export function createEvidenceAnnotator(): (row: EvidenceAttempt) => EvidenceAttempt {
  // day → evidence key → EXPOSED | GLIMPSED. Nested maps rather than one set of
  // `${day} ${key}` strings (CPU budget, §3.3.1): day keys and evidence keys are
  // long-lived strings with cached hashes, so a row builds and hashes no string.
  // Rows of one day mostly arrive together, so the day's map is reused.
  const byDay = new Map<string, Map<string, number>>();
  let lastDay: string | undefined;
  let slots: Map<string, number> | undefined;
  return (row) => {
    if (row.day !== lastDay || slots === undefined) {
      slots = byDay.get(row.day);
      if (!slots) byDay.set(row.day, (slots = new Map()));
      lastDay = row.day;
    }
    const seen = slots.get(row.evidenceKey) ?? 0;
    row.sameDayRepeat = (seen & EXPOSED) !== 0;
    const mark = row.kind === "plan" ? GLIMPSED : EXPOSED;
    if ((seen & mark) === 0) slots.set(row.evidenceKey, seen | mark);
    row.assisted = isAssistedAnswer(row, seen !== 0);
    return row;
  };
}

/** `createEvidenceAnnotator` over a whole ascending history; the inputs are not changed. */
export function annotateEvidence(rows: readonly EvidenceAttemptInput[]): EvidenceAttempt[] {
  const annotate = createEvidenceAnnotator();
  return rows.map((row) => annotate({ ...row, sameDayRepeat: false, assisted: false }));
}

/* --------------------------- calibration (§3.3.3) --------------------------- */

/** Below this many rated answers the Progress page shows no calibration chart. */
export const CALIBRATION_MIN_RATED = 20;
/** A confidence level with fewer answers shows "för få svar" instead of a share. */
export const CALIBRATION_MIN_PER_LEVEL = 5;

/**
 * Caption thresholds (pedagogy spec §3.3.3).
 *
 * `over`'s two-step floor (review G, minor 3, re-review New Breakage #1): a
 * small sample only trips it at a share well below the level's own diagonal
 * (`level / 4`), so 5-9 answers don't read as over-confident from one
 * unlucky miss; a larger sample trips it closer to the diagonal. Both steps
 * are offsets *below the diagonal*, not absolute shares — the first fix
 * round used level 4's absolute floors (0.6, 0.85) for every level, which
 * matched level 4 exactly (diagonal 1.0) but was far too strict at level 3
 * (diagonal 0.75): a perfectly calibrated "Ganska säker" student read as
 * over-confident with probability 75.6 % at n = 10 and 90.2 % at n = 30,
 * leaving `good` essentially unreachable there. `OVER_LOOSE_GAP` and
 * `GOOD_MAX_GAP` (the strict step reuses the `good` gap, below) reproduce
 * today's level-4 floors exactly (1.0 − 0.4 = 0.6, 1.0 − 0.15 = 0.85) and
 * give level 3 (diagonal 0.75) 0.35 and 0.6: false-`over` risk for a
 * perfectly calibrated "Ganska säker" student drops to 1.6 % at n = 5, 7.8 %
 * at n = 10 and 2.2 % at n = 30, and stays at or below about 16 % for every
 * n up to 40 (peak at n = 12, where 7/12 falls just under 0.6). Checked per
 * level (`OVER_LEVELS`), not pooled — see `isOverConfident`.
 */
const OVER_LOOSE_MIN_N = CALIBRATION_MIN_PER_LEVEL; // 5
/** Offset below the diagonal for the loose (n ≥ `OVER_LOOSE_MIN_N`) step. */
const OVER_LOOSE_GAP = 0.4;
const OVER_STRICT_MIN_N = 10;
/**
 * `over` is checked at "Ganska säker" and "Helt säker" individually (review
 * G, minor 5): pooling only "Helt säker" left a clearly miscalibrated
 * "Ganska säker" level (e.g. 0/30) reading as `unclear` instead, with copy
 * that wrongly promised more data would help.
 */
const OVER_LEVELS: readonly ConfidenceLevel[] = [3, 4];
const UNDER_MIN_UNSURE = 10;
const UNDER_FROM_SHARE = 0.7;
/**
 * "Stämmer väl" threshold (bug UX-09 fix): the gap between each rated
 * level's actual share right and its expected share (`level / 4`, the
 * chart's own diagonal) must sit under this, for every level with enough
 * answers to show one (`CALIBRATION_MIN_PER_LEVEL`) — checked per level
 * (`Array.prototype.every`), not as a mean across levels.
 */
const GOOD_MAX_GAP = 0.15;
/** Float slack for the gap comparison (e.g. 17/20 - 1 rounds to -0.15000000000000002). */
const GAP_TOLERANCE = 1e-9;

const LEVELS: readonly ConfidenceLevel[] = [1, 2, 3, 4];

export interface CalibrationLevel {
  level: ConfidenceLevel;
  /** Rated answers at this level. */
  n: number;
  /** Of those, the ones graded right. Not `correct`: that is a redaction-gated key. */
  rightCount: number;
}

export interface Calibration {
  rated: number;
  /** Always the four levels 1–4, in order. */
  levels: CalibrationLevel[];
}

/**
 * `unclear` (bug UX-09): neither a demonstrated over/under-confidence
 * pattern, nor enough evidence that confidence tracks results — the honest
 * middle ground `calibrationCaption` used to collapse into `good`.
 */
export type CalibrationCaption = "over" | "under" | "good" | "unclear";

/**
 * Whether a row belongs to the problem series: a practice answer graded
 * automatically (MC and numeric; self-graded open answers are graded after the
 * solution is shown, which biases their right-rate upward), with a rating,
 * that is the first exposure of its key that day. Hinted answers count: the
 * rating is the confidence at the moment of answering. Sequence tasks and
 * completions do not: they share one key per sequence, are predictions made
 * before any instruction or assisted by design.
 */
export function countsForCalibration(
  attempt: EvidenceAttempt,
): attempt is EvidenceAttempt & { confidence: ConfidenceLevel } {
  return (
    attempt.kind === "answer" &&
    attempt.mode === "practice" &&
    attempt.gradedBy === "auto" &&
    attempt.confidence !== null &&
    !attempt.sameDayRepeat &&
    attempt.sequenceRole !== "task" &&
    attempt.sequenceRole !== "completion"
  );
}

/** Rated and right answers per confidence level (pedagogy spec §3.3.3, problem series). */
export function calibration(attempts: readonly EvidenceAttempt[]): Calibration {
  const levels: CalibrationLevel[] = LEVELS.map((level) => ({ level, n: 0, rightCount: 0 }));
  let rated = 0;
  for (const attempt of attempts) {
    if (!countsForCalibration(attempt)) continue;
    const entry = levels[attempt.confidence - 1];
    entry.n += 1;
    if (attempt.correct === true) entry.rightCount += 1;
    rated += 1;
  }
  return { rated, levels };
}

function levelOf(c: Calibration, level: ConfidenceLevel): CalibrationLevel {
  return c.levels.find((l) => l.level === level) ?? { level, n: 0, rightCount: 0 };
}

/**
 * Whether one rated level is over-confident on its own (review G, minor 3/5,
 * re-review New Breakage #1): `OVER_LOOSE_MIN_N` answers at a share more
 * than `OVER_LOOSE_GAP` below the level's own diagonal (`level / 4`), or
 * `OVER_STRICT_MIN_N` at more than `GOOD_MAX_GAP` below it. The looser step
 * catches a level that is badly miscalibrated on a small sample (e.g. 5/5
 * wrong); the stricter step needs more evidence to flag a level that is only
 * moderately off. Both steps are relative to the diagonal, not an absolute
 * share, so they scale correctly across levels — see the constants above.
 */
function isOverConfident(level: CalibrationLevel): boolean {
  if (level.n === 0) return false;
  const share = level.rightCount / level.n;
  const diagonal = level.level / 4;
  return (
    (level.n >= OVER_LOOSE_MIN_N && share < diagonal - OVER_LOOSE_GAP) ||
    (level.n >= OVER_STRICT_MIN_N && share < diagonal - GOOD_MAX_GAP)
  );
}

/**
 * The caption, first match wins (pedagogy spec §3.3.3, amended by bug UX-09
 * and review G): `over` when "Ganska säker" or "Helt säker" is individually
 * over-confident (`isOverConfident`); `under` when at least 10 answers were
 * rated "Gissar" or "Osäker" and 70 % or more of those (pooled) were right;
 * `good` when every rated level tracks its expected share closely enough;
 * otherwise `unclear`.
 *
 * `good` used to be the fallback whenever neither `over` nor `under` fired —
 * including for a student who was wrong at every level, "Helt säker"
 * included, whenever "Helt säker" had one answer fewer than `over`'s old,
 * stricter floor. `good` is now an earned verdict: it requires positive
 * evidence that confidence and results actually line up, not just the
 * absence of a strong contrary signal. A calibration with no rated answers
 * at all now reads `unclear` (falls out of the same check below — there are
 * no levels with `CALIBRATION_MIN_PER_LEVEL` answers to track), not `good`:
 * `CalibrationCard` never renders a caption below `CALIBRATION_MIN_RATED`
 * anyway, so this only matters to callers of the pure function.
 */
export function calibrationCaption(c: Calibration): CalibrationCaption {
  if (OVER_LEVELS.some((level) => isOverConfident(levelOf(c, level)))) {
    return "over";
  }

  const guessing = levelOf(c, 1);
  const unsure = levelOf(c, 2);
  const lowN = guessing.n + unsure.n;
  const lowShare = lowN > 0 ? (guessing.rightCount + unsure.rightCount) / lowN : null;
  if (lowN >= UNDER_MIN_UNSURE && lowShare !== null && lowShare >= UNDER_FROM_SHARE) {
    return "under";
  }

  const ratedLevels = c.levels.filter((l) => l.n >= CALIBRATION_MIN_PER_LEVEL);
  const tracksConfidence =
    ratedLevels.length > 0 &&
    ratedLevels.every(
      (l) => Math.abs(l.rightCount / l.n - l.level / 4) <= GOOD_MAX_GAP + GAP_TOLERANCE,
    );
  return tracksConfidence ? "good" : "unclear";
}

/* ------------------------- classification (§2.4) ------------------------- */

/** Today schedules a missed item this many calendar days after its last exposure. */
export const RETEST_GAP_DAYS = 2;
/** Plus a minimum elapsed time, so 23:58 → 00:03 is not "a day later". */
export const MIN_RETEST_GAP_HOURS = 12;
/** A secure topic is re-tested this many days after its last exposure. */
export const MAINTENANCE_GAP_DAYS = 14;
/** "Coming back" lists re-tests due up to today + this many days. */
export const COMING_BACK_HORIZON_DAYS = 7;
/** "Coming back" shows at most this many rows (§3.3.3). */
export const COMING_BACK_MAX_ROWS = 6;
/** Recurring slips are counted over the last this many local days, today included. */
export const SLIP_WINDOW_DAYS = 30;
/** A slip kind is shown from this many (key, day) occurrences. */
export const RECURRING_SLIP_MIN = 2;
/** At most this many slip kinds are shown, each with at most this many topics. */
export const RECURRING_SLIP_MAX_KINDS = 5;
export const RECURRING_SLIP_MAX_TOPICS = 3;

const MIN_RETEST_GAP_MS = MIN_RETEST_GAP_HOURS * 3_600_000;

/**
 * `content/schema.ts#slipKinds` order (a test pins the two together; the
 * value list stays out of this file so the client bundle carries no zod).
 * `Record` makes a missing or extra kind a compile error.
 */
const SLIP_RANK: Record<SlipKind, number> = {
  "power-of-ten": 0,
  unit: 1,
  sign: 2,
  factor: 3,
  "classical-for-relativistic": 4,
  "wrong-principle": 5,
  "plan-miss": 6,
  other: 7,
};
/** The slip kinds in enum order. */
export const SLIP_ORDER = Object.keys(SLIP_RANK) as SlipKind[];

/** One selectable question, as the pools see it. */
export interface EvidencePoolItem {
  id: string;
  /** Primary topic only: secondary topics never enter a pool. */
  topic: string;
  familyId: string | null;
  type: QuestionType;
}

/** One taxonomy topic and its primary-topic pool. */
export interface EvidenceTopicPool {
  id: string;
  chapter: number;
  cursory: boolean;
  /** Non-cursory with at least one selectable primary item (Y&F §10). */
  eligible: boolean;
  items: readonly EvidencePoolItem[];
}

/**
 * Primary-topic pools of the selectable bank (§2.4, §3.3.3), built once per
 * content index (`server/src/content/evidencePools.ts`). "Unexposed sibling",
 * "fresh item" and the "Coming back" variant read them.
 */
export interface EvidencePools {
  /** Every taxonomy topic, in course (`topics.json`) order. */
  topics: readonly EvidenceTopicPool[];
  topic: ReadonlyMap<string, EvidenceTopicPool>;
  /** Taxonomy position of each topic id. */
  rank: ReadonlyMap<string, number>;
  item: ReadonlyMap<string, EvidencePoolItem>;
  /** Selectable members per test-bank family. */
  familySize: ReadonlyMap<string, number>;
  /** Evidence key (`familyId ?? id`) → primary topic, for the `origin.retestOf` check (§2.2). */
  keyTopic: ReadonlyMap<string, string>;
}

export interface EvidencePoolTopic {
  id: string;
  chapter: number;
  cursory: boolean;
}

export type EvidencePoolQuestion = Pick<Question, "id" | "type" | "topic" | "source">;

/**
 * Pools from the taxonomy and the selectable questions (`content.selectable()`,
 * already free of retired, draft and exam-sourced items). Each question joins
 * its primary topic's pool only; a question whose topic is not in the taxonomy
 * is ignored.
 */
export function buildEvidencePools(
  topics: readonly EvidencePoolTopic[],
  selectable: readonly EvidencePoolQuestion[],
): EvidencePools {
  const byTopic = new Map<string, EvidencePoolItem[]>(topics.map((t) => [t.id, []]));
  const item = new Map<string, EvidencePoolItem>();
  const familySize = new Map<string, number>();
  const keyTopic = new Map<string, string>();
  for (const q of selectable) {
    const list = byTopic.get(q.topic);
    if (!list || item.has(q.id)) continue;
    const familyId = familyIdOf(q);
    const entry: EvidencePoolItem = { id: q.id, topic: q.topic, familyId, type: q.type };
    list.push(entry);
    item.set(q.id, entry);
    if (familyId !== null) familySize.set(familyId, (familySize.get(familyId) ?? 0) + 1);
    keyTopic.set(familyId ?? q.id, q.topic);
  }
  const pools: EvidenceTopicPool[] = topics.map((t) => {
    const items = byTopic.get(t.id) ?? [];
    return {
      id: t.id,
      chapter: t.chapter,
      cursory: t.cursory,
      eligible: !t.cursory && items.length > 0,
      items,
    };
  });
  return {
    topics: pools,
    topic: new Map(pools.map((p) => [p.id, p])),
    rank: new Map(pools.map((p, i) => [p.id, i])),
    item,
    familySize,
    keyTopic,
  };
}

/** Topic states (§2.4), weakest first. */
export type TopicState = "not-started" | "tried" | "with-help" | "on-your-own" | "secure";
export const TOPIC_STATES: readonly TopicState[] = [
  "not-started",
  "tried",
  "with-help",
  "on-your-own",
  "secure",
];

/** What the history says about one evidence key. */
export interface KeyEvidence {
  key: string;
  topic: string;
  chapter: number;
  questionType: QuestionType;
  lastExposureAt: number;
  lastExposureDay: string;
  /** At least one answer row (set tasks included). */
  answered: boolean;
  /** At least one unaided success. */
  solvedUnaided: boolean;
  /** Successes, all assisted (retries and completions included). */
  solvedWithHelp: boolean;
  /**
   * At least one delayed retrieval counted for this key, and at least one of
   * them passed. A delayed-retrieval row counts once, for the key it re-tests
   * (`origin.retestOf` first, then its own key, then the key it stands in
   * for under rule (i)'s same-topic clause), so one answer never adds several
   * re-tests to the summary.
   */
  retested: boolean;
  retestPassed: boolean;
  /** The same for the subset carrying `origin.retestOf` (Today's scheduled re-tests). */
  scheduledRetested: boolean;
  scheduledRetestPassed: boolean;
  /** Its latest delayed retrieval passed on a fresh item. */
  secure: boolean;
  openMiss: boolean;
  /** An open miss kept open by at least one practice row. */
  practiceMiss: boolean;
  /**
   * Due day of an open miss, else null: `RETEST_GAP_DAYS` after its last
   * exposure or its latest failed delayed retrieval, whichever is later.
   */
  dueDay: string | null;
}

/** One topic: its state and the counts behind the course-map detail. */
export interface TopicEvidence {
  topicId: string;
  chapter: number;
  eligible: boolean;
  /** Null when the topic is not eligible (cursory, or no selectable primary item). */
  state: TopicState | null;
  /** Keys with an answer row ("försökt"). */
  items: number;
  /** Keys with an unaided success ("på egen hand"). */
  unaidedItems: number;
  /** Keys with successes, all assisted ("med hjälp"). */
  assistedOnlyItems: number;
  lastExposureAt: number | null;
  lastExposureDay: string | null;
  /** Earliest due day among the topic's `dueRetests` entries. */
  retestDue: string | null;
}

/** The per-student summary (§2.4); every count is per key. */
export interface EvidenceSummary {
  topicsStarted: number;
  topicsEligible: number;
  solvedUnaided: number;
  solvedWithHelp: number;
  retestsTaken: number;
  retestsPassed: number;
  scheduledRetestsTaken: number;
  scheduledRetestsPassed: number;
}

/** An open miss (practice or exam-only). */
export interface OpenMiss {
  key: string;
  topic: string;
  chapter: number;
  dueDay: string;
  /** Kept open by at least one practice row; exam-only misses are left to the exam wrapper. */
  practice: boolean;
  lastExposureAt: number;
  /** Share of points on the latest row keeping it open; a give-up is 0. */
  scoreRatio: number;
}

export type DueRetestReason = "miss" | "confirm" | "maintain";
/** What Today will serve: an unexposed sibling, another unexposed topic item, or a seen item. */
export type RetestVariant = "sibling" | "new" | "seen";

/** One entry of the due re-test list Progress "Coming back" and Today share (§12 C19). */
export interface DueRetest {
  reason: DueRetestReason;
  /** The key to re-test, never a `seq:` key; Today tags the served item `origin.retestOf = key`. */
  key: string;
  /** Primary topic. */
  topic: string;
  chapter: number;
  dueDay: string;
  variant: RetestVariant;
  /** `miss`: share of points on the latest row keeping it open (a give-up 0); otherwise null. */
  scoreRatio: number | null;
  /** `miss`: the key's last exposure; `confirm`/`maintain`: the topic's. */
  lastExposureAt: number;
}

export interface RecurringSlip {
  kind: SlipKind;
  /** Distinct (key, day) occurrences inside the window. */
  count: number;
  /** Up to three primary topics, most frequent first, then course order. */
  topics: string[];
}

export type ComingBackRow =
  | {
      source: "problem";
      reason: DueRetestReason;
      key: string;
      topic: string;
      chapter: number;
      /** Overdue entries read as today. */
      dueDay: string;
      variant: RetestVariant;
    }
  | { source: "flashcards"; dueDay: string; cards: number };

export interface EvidenceOptions {
  /** Today's local day key (`dayKey(now, tz)`), the same zone as every row's `day`. */
  today: string;
  /** Due day of each flashcard (overdue ones included) for "Coming back"; default none. */
  cardDueDays?: readonly string[];
}

export interface EvidenceClassification {
  keys: ReadonlyMap<string, KeyEvidence>;
  /** Every taxonomy topic, in course order. */
  topics: ReadonlyMap<string, TopicEvidence>;
  summary: EvidenceSummary;
  /** Every open miss, practice and exam-only, oldest due day first. */
  openMisses: OpenMiss[];
  dueRetests: DueRetest[];
  comingBack: ComingBackRow[];
  recurringSlips: RecurringSlip[];
}

type Retrieval = "pass-fresh" | "pass" | "fail";

interface KeyAcc {
  key: string;
  topic: string;
  /** The accumulator of `topic` (the topic of the row that first exposed the key). */
  topicAcc: TopicAcc;
  chapter: number;
  type: QuestionType;
  seq: boolean;
  family: boolean;
  lastAt: number;
  lastDay: string;
  /**
   * The day an open miss's due day counts from, when later than `lastDay`: a
   * failed delayed retrieval on another item (Today's substitute or a
   * same-topic stand-in) moves it without exposing the key.
   */
  dueFrom: string;
  answered: boolean;
  success: boolean;
  unaided: boolean;
  open: boolean;
  openPractice: boolean;
  openRatio: number;
  retested: boolean;
  retestPassed: boolean;
  scheduledRetested: boolean;
  scheduledPassed: boolean;
  latest: Retrieval | null;
  /** The question of the key's latest exposure (already in `exposedIds`); null before one. */
  lastQuestionId: string | null;
}

interface TopicAcc {
  chapter: number;
  /** The topic's last exposure; the row that creates the accumulator is its first. */
  lastAt: number;
  lastDay: string;
  answered: boolean;
  success: boolean;
  unaided: boolean;
  /** Row index of the latest passed delayed retrieval on a fresh item; -1 none. */
  lastPassFresh: number;
  /** Row index of the latest failed or assisted delayed retrieval; -1 none. */
  lastFail: number;
  latestUnaidedKey: string | null;
  latestPassedKey: string | null;
  /** Exposed non-sequence keys of the topic per question type (rule (i)'s same-topic clause). */
  byType: Partial<Record<QuestionType, KeyAcc[]>>;
}

const REASON_ORDER: Record<DueRetestReason, number> = { miss: 0, confirm: 1, maintain: 2 };

function scoreRatioOf(a: EvidenceAttempt): number {
  if (a.kind !== "answer" || a.score === null || a.maxScore === null || !(a.maxScore > 0)) {
    return 0;
  }
  return Math.min(1, Math.max(0, a.score / a.maxScore));
}

function increment(map: Map<string, number>, key: string): void {
  map.set(key, (map.get(key) ?? 0) + 1);
}

/**
 * The §2.4 classification in one pass over an ascending history (one row per
 * attempt whose question still resolves, `day` precomputed, `assisted` from
 * `annotateEvidence`): per-key facts, topic states, the summary, open misses,
 * the due re-test list, "Coming back" and recurring slips. O(n) in rows, times
 * the keys of one topic and type for rule (i)'s same-topic clause.
 *
 * - **Exposure**: an answer, retry or reveal row of any mode. Plan rows are
 *   glimpses and are skipped here (their only effect, an assisted same-day
 *   answer, is already in `assisted`).
 * - **Delayed retrieval** of key k: an answer row r, (i) on k, carrying
 *   `origin.retestOf = k`, or on k's primary topic with k's type when k has no
 *   unexposed sibling; (ii) the first exposure of r's own key that day; (iii)
 *   k last exposed on an earlier day and at least `MIN_RETEST_GAP_HOURS`
 *   before r. Rows on `seq:` keys and follow-ups (§10: same-day follow-ups
 *   are never delayed retrievals) never are one, and `seq:` keys are never
 *   re-tested.
 * - **Open miss** k: an answer or reveal row on k that is not an unaided
 *   success with no later passed re-test of k; due `RETEST_GAP_DAYS` after
 *   k's last exposure or its latest failed delayed retrieval, whichever is
 *   later, so a failed re-test reopens it two days on, also when it was taken
 *   on another item (Today's substitute, a same-topic stand-in).
 */
export function classifyEvidence(
  attempts: readonly EvidenceAttempt[],
  pools: EvidencePools,
  options: EvidenceOptions,
): EvidenceClassification {
  const { keys, topics, slips, hasUnexposedSibling, hasUnexposedInTopic } = scanEvidence(
    attempts,
    pools,
    options.today,
  );

  const variantFor = (k: KeyAcc): RetestVariant =>
    hasUnexposedSibling(k) ? "sibling" : hasUnexposedInTopic(k.topic) ? "new" : "seen";
  const rankOf = (topic: string) => pools.rank.get(topic) ?? Number.MAX_SAFE_INTEGER;

  const keyOut = new Map<string, KeyEvidence>();
  const openMisses: OpenMiss[] = [];
  const due: DueRetest[] = [];
  const practiceMissTopics = new Set<string>();
  const counts = new Map<string, { items: number; unaided: number; assisted: number }>();
  const summary: EvidenceSummary = {
    topicsStarted: 0,
    topicsEligible: 0,
    solvedUnaided: 0,
    solvedWithHelp: 0,
    retestsTaken: 0,
    retestsPassed: 0,
    scheduledRetestsTaken: 0,
    scheduledRetestsPassed: 0,
  };
  for (const k of keys.values()) {
    const dueDay = k.open
      ? addDays(k.dueFrom > k.lastDay ? k.dueFrom : k.lastDay, RETEST_GAP_DAYS)
      : null;
    const withHelp = k.success && !k.unaided;
    keyOut.set(k.key, {
      key: k.key,
      topic: k.topic,
      chapter: k.chapter,
      questionType: k.type,
      lastExposureAt: k.lastAt,
      lastExposureDay: k.lastDay,
      answered: k.answered,
      solvedUnaided: k.unaided,
      solvedWithHelp: withHelp,
      retested: k.retested,
      retestPassed: k.retestPassed,
      scheduledRetested: k.scheduledRetested,
      scheduledRetestPassed: k.scheduledPassed,
      secure: k.latest === "pass-fresh",
      openMiss: k.open,
      practiceMiss: k.open && k.openPractice,
      dueDay,
    });
    let c = counts.get(k.topic);
    if (!c) counts.set(k.topic, (c = { items: 0, unaided: 0, assisted: 0 }));
    if (k.answered) c.items += 1;
    if (k.unaided) c.unaided += 1;
    if (withHelp) c.assisted += 1;
    if (k.unaided) summary.solvedUnaided += 1;
    if (withHelp) summary.solvedWithHelp += 1;
    if (k.retested) summary.retestsTaken += 1;
    if (k.retestPassed) summary.retestsPassed += 1;
    if (k.scheduledRetested) summary.scheduledRetestsTaken += 1;
    if (k.scheduledPassed) summary.scheduledRetestsPassed += 1;
    if (dueDay === null) continue;
    openMisses.push({
      key: k.key,
      topic: k.topic,
      chapter: k.chapter,
      dueDay,
      practice: k.openPractice,
      lastExposureAt: k.lastAt,
      scoreRatio: k.openRatio,
    });
    if (!k.openPractice) continue;
    practiceMissTopics.add(k.topic);
    due.push({
      reason: "miss",
      key: k.key,
      topic: k.topic,
      chapter: k.chapter,
      dueDay,
      variant: variantFor(k),
      scoreRatio: k.openRatio,
      lastExposureAt: k.lastAt,
    });
  }

  const stateOf = (t: TopicAcc | undefined): TopicState => {
    if (!t) return "not-started";
    if (t.lastPassFresh >= 0 && t.lastPassFresh > t.lastFail) return "secure";
    if (t.unaided) return "on-your-own";
    if (t.success) return "with-help";
    if (t.answered) return "tried";
    return "not-started";
  };

  const states = new Map<string, TopicState | null>();
  for (const p of pools.topics) {
    const t = topics.get(p.id);
    const state = p.eligible ? stateOf(t) : null;
    states.set(p.id, state);
    if (state === null) continue;
    summary.topicsEligible += 1;
    if (state !== "not-started") summary.topicsStarted += 1;
    if (!t) continue;
    let reason: DueRetestReason | null = null;
    let key: string | null = null;
    if (state === "on-your-own" && !practiceMissTopics.has(p.id)) {
      reason = "confirm";
      key = t.latestUnaidedKey;
    } else if (state === "secure") {
      reason = "maintain";
      key = t.latestPassedKey;
    }
    const k = key === null ? undefined : keys.get(key);
    if (reason === null || !k) continue;
    due.push({
      reason,
      key: k.key,
      topic: p.id,
      chapter: p.chapter,
      dueDay: addDays(t.lastDay, reason === "confirm" ? RETEST_GAP_DAYS : MAINTENANCE_GAP_DAYS),
      variant: variantFor(k),
      scoreRatio: null,
      lastExposureAt: t.lastAt,
    });
  }

  const byDue = (a: { dueDay: string; topic: string; key: string }, b: typeof a) =>
    (a.dueDay < b.dueDay ? -1 : a.dueDay > b.dueDay ? 1 : 0) ||
    rankOf(a.topic) - rankOf(b.topic) ||
    (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
  due.sort(
    (a, b) =>
      (a.dueDay < b.dueDay ? -1 : a.dueDay > b.dueDay ? 1 : 0) ||
      REASON_ORDER[a.reason] - REASON_ORDER[b.reason] ||
      byDue(a, b),
  );
  openMisses.sort(byDue);

  const retestDue = new Map<string, string>();
  for (const e of due) {
    const current = retestDue.get(e.topic);
    if (current === undefined || e.dueDay < current) retestDue.set(e.topic, e.dueDay);
  }

  const topicOut = new Map<string, TopicEvidence>();
  const describe = (id: string, chapter: number, eligible: boolean): void => {
    const t = topics.get(id);
    const c = counts.get(id);
    topicOut.set(id, {
      topicId: id,
      chapter,
      eligible,
      state: states.get(id) ?? null,
      items: c?.items ?? 0,
      unaidedItems: c?.unaided ?? 0,
      assistedOnlyItems: c?.assisted ?? 0,
      lastExposureAt: t?.lastAt ?? null,
      lastExposureDay: t?.lastDay ?? null,
      retestDue: retestDue.get(id) ?? null,
    });
  };
  for (const p of pools.topics) describe(p.id, p.chapter, p.eligible);
  for (const [id, t] of topics) if (!topicOut.has(id)) describe(id, t.chapter, false);

  return {
    keys: keyOut,
    topics: topicOut,
    summary,
    openMisses,
    dueRetests: due,
    comingBack: comingBack(due, pools, options),
    recurringSlips: slips.result(pools),
  };
}

/**
 * `classifyEvidence`'s single pass over the rows (the rules in its comment):
 * per-key and per-topic facts, exposure counts and slips. A function of its
 * own (CPU budget, §3.3.1): the engine optimises this small hot loop several
 * calls sooner than the whole classification, which is what the first
 * requests on a fresh isolate pay for; warm, the cost is the same.
 */
function scanEvidence(attempts: readonly EvidenceAttempt[], pools: EvidencePools, today: string) {
  const keys = new Map<string, KeyAcc>();
  const topics = new Map<string, TopicAcc>();
  const slips = slipCounter(today);
  const exposedIds = new Set<string>();
  const exposedInFamily = new Map<string, number>();
  const exposedInTopic = new Map<string, number>();

  const topicAcc = (a: EvidenceAttempt): TopicAcc => {
    let t = topics.get(a.topic);
    if (!t) {
      t = {
        chapter: a.chapter,
        lastAt: a.createdAt,
        lastDay: a.day,
        answered: false,
        success: false,
        unaided: false,
        lastPassFresh: -1,
        lastFail: -1,
        latestUnaidedKey: null,
        latestPassedKey: null,
        byType: {},
      };
      topics.set(a.topic, t);
    }
    return t;
  };
  const hasUnexposedSibling = (k: KeyAcc): boolean =>
    k.family && (pools.familySize.get(k.key) ?? 0) > (exposedInFamily.get(k.key) ?? 0);
  const hasUnexposedInTopic = (topic: string): boolean =>
    (pools.topic.get(topic)?.items.length ?? 0) > (exposedInTopic.get(topic) ?? 0);
  const delayedFor = (k: KeyAcc, a: EvidenceAttempt): boolean =>
    k.lastDay < a.day && a.createdAt - k.lastAt >= MIN_RETEST_GAP_MS;
  /** "A different item or variant where one exists", judged before `a` is recorded. */
  const isFresh = (a: EvidenceAttempt): boolean => {
    if (!exposedIds.has(a.questionId)) return true;
    if (a.evidenceKey !== a.questionId) {
      return (
        (pools.familySize.get(a.evidenceKey) ?? 0) <= (exposedInFamily.get(a.evidenceKey) ?? 0)
      );
    }
    return !hasUnexposedInTopic(a.topic);
  };
  // The keys a row is a delayed retrieval of, in `targets[0, n)`: one buffer
  // for every row (CPU budget, §3.3.1), read before the next row refills it.
  const targets: KeyAcc[] = [];
  const retrievalTargets = (a: EvidenceAttempt, t: TopicAcc, own: KeyAcc | undefined) => {
    let n = 0;
    if (own && delayedFor(own, a)) targets[n++] = own;
    const named =
      a.retestOf !== null && a.retestOf !== a.evidenceKey && !isSequenceKey(a.retestOf)
        ? keys.get(a.retestOf)
        : undefined;
    if (named && delayedFor(named, a)) targets[n++] = named;
    for (const k of t.byType[a.questionType] ?? []) {
      if (k === own || k === named) continue;
      if (!hasUnexposedSibling(k) && delayedFor(k, a)) targets[n++] = k;
    }
    return n;
  };
  /** The one key a delayed-retrieval row counts for in the summary (see `KeyEvidence.retested`). */
  const countedFor = (n: number, a: EvidenceAttempt, own: KeyAcc | undefined) => {
    for (let j = 0; j < n; j++) if (targets[j].key === a.retestOf) return targets[j];
    if (own && targets[0] === own) return own;
    let best = targets[0];
    for (let j = 0; j < n; j++) {
      const k = targets[j];
      const better =
        k.open !== best.open
          ? k.open
          : k.lastAt !== best.lastAt
            ? k.lastAt < best.lastAt
            : k.key < best.key;
      if (better) best = k;
    }
    return best;
  };

  for (let i = 0; i < attempts.length; i++) {
    const a = attempts[i];
    if (a.slips.length > 0) slips.add(a);
    if (a.kind === "plan") continue;
    // Hot loop (CPU budget, §3.3.1): a row on a known key takes the key's
    // sequence flag and, on the key's topic, its topic accumulator.
    let own = keys.get(a.evidenceKey);
    const seq = own !== undefined ? own.seq : isSequenceKey(a.evidenceKey);
    const t = own !== undefined && own.topic === a.topic ? own.topicAcc : topicAcc(a);
    const isTask = a.sequenceRole === "task";
    const unaidedSuccess = a.kind === "answer" && a.correct === true && !a.assisted && !isTask;

    // 1. Delayed retrieval, judged against the exposures before this row.
    if (a.kind === "answer" && !seq && a.followUpKind === null && own?.lastDay !== a.day) {
      const n = retrievalTargets(a, t, own);
      if (n > 0) {
        const fresh = isFresh(a);
        const counted = countedFor(n, a, own);
        counted.retested = true;
        if (a.retestOf !== null) counted.scheduledRetested = true;
        if (unaidedSuccess) {
          counted.retestPassed = true;
          if (a.retestOf !== null) counted.scheduledPassed = true;
          t.latestPassedKey = counted.key;
          if (fresh) t.lastPassFresh = i;
        } else {
          t.lastFail = i;
        }
        const outcome: Retrieval = unaidedSuccess ? (fresh ? "pass-fresh" : "pass") : "fail";
        for (let j = 0; j < n; j++) {
          const k = targets[j];
          k.latest = outcome;
          if (unaidedSuccess) k.open = false;
          else if (k.open) k.dueFrom = a.day; // a failed re-test reopens the miss (§2.4)
        }
      }
    }

    // 2. Exposure.
    if (!own) {
      own = {
        key: a.evidenceKey,
        topic: a.topic,
        topicAcc: t,
        chapter: a.chapter,
        type: a.questionType,
        seq,
        family: !seq && a.evidenceKey !== a.questionId,
        lastAt: a.createdAt,
        lastDay: a.day,
        dueFrom: a.day,
        answered: false,
        success: false,
        unaided: false,
        open: false,
        openPractice: false,
        openRatio: 0,
        retested: false,
        retestPassed: false,
        scheduledRetested: false,
        scheduledPassed: false,
        latest: null,
        lastQuestionId: null,
      };
      keys.set(own.key, own);
      if (!seq) (t.byType[a.questionType] ??= []).push(own);
    }
    own.lastAt = a.createdAt;
    own.lastDay = a.day;
    t.lastAt = a.createdAt;
    t.lastDay = a.day;
    // A row on the question its key last exposed skips the set lookup (CPU budget, §3.3.1).
    if (own.lastQuestionId !== a.questionId) {
      own.lastQuestionId = a.questionId;
      if (!exposedIds.has(a.questionId)) {
        exposedIds.add(a.questionId);
        const item = pools.item.get(a.questionId);
        if (item) {
          if (item.familyId !== null) increment(exposedInFamily, item.familyId);
          increment(exposedInTopic, item.topic);
        }
      }
    }

    // 3. Answers, successes and misses.
    if (a.kind === "answer") {
      own.answered = true;
      t.answered = true;
    }
    if ((a.kind === "answer" || a.kind === "retry") && a.correct === true && !isTask) {
      own.success = true;
      t.success = true;
    }
    if (unaidedSuccess) {
      own.unaided = true;
      t.unaided = true;
      if (!seq) t.latestUnaidedKey = own.key;
    }
    if (!seq && !unaidedSuccess && (a.kind === "answer" || a.kind === "reveal")) {
      if (!own.open) {
        own.open = true;
        own.openPractice = false;
      }
      if (a.mode === "practice") own.openPractice = true;
      own.openRatio = scoreRatioOf(a);
    }
  }

  return { keys, topics, slips, hasUnexposedSibling, hasUnexposedInTopic };
}

/** The due re-test list of `classifyEvidence` (§2.4), for callers that need nothing else. */
export function dueRetests(
  attempts: readonly EvidenceAttempt[],
  pools: EvidencePools,
  today: string,
): DueRetest[] {
  return classifyEvidence(attempts, pools, { today }).dueRetests;
}

/**
 * Progress "Coming back" (§3.3.3): every due re-test up to today +
 * `COMING_BACK_HORIZON_DAYS` (overdue ones read as today) plus one collapsed
 * flashcard row per day, sorted by day, problems before flashcards, then
 * course order; at most `COMING_BACK_MAX_ROWS`.
 */
export function comingBack(
  due: readonly DueRetest[],
  pools: Pick<EvidencePools, "rank">,
  options: EvidenceOptions,
): ComingBackRow[] {
  const { today } = options;
  const last = addDays(today, COMING_BACK_HORIZON_DAYS);
  const rows: ComingBackRow[] = [];
  for (const e of due) {
    const dueDay = e.dueDay < today ? today : e.dueDay;
    if (dueDay > last) continue;
    rows.push({
      source: "problem",
      reason: e.reason,
      key: e.key,
      topic: e.topic,
      chapter: e.chapter,
      dueDay,
      variant: e.variant,
    });
  }
  const cards = new Map<string, number>();
  for (const day of options.cardDueDays ?? []) {
    const dueDay = day < today ? today : day;
    if (dueDay <= last) increment(cards, dueDay);
  }
  for (const [dueDay, n] of cards) rows.push({ source: "flashcards", dueDay, cards: n });

  const rank = (row: ComingBackRow) =>
    row.source === "problem" ? (pools.rank.get(row.topic) ?? Number.MAX_SAFE_INTEGER) : 0;
  rows.sort((a, b) => {
    if (a.dueDay !== b.dueDay) return a.dueDay < b.dueDay ? -1 : 1;
    if (a.source !== b.source) return a.source === "problem" ? -1 : 1;
    if (a.source === "flashcards" || b.source === "flashcards") return 0;
    return (
      rank(a) - rank(b) ||
      REASON_ORDER[a.reason] - REASON_ORDER[b.reason] ||
      (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
    );
  });
  return rows.slice(0, COMING_BACK_MAX_ROWS);
}

/**
 * Recurring slips (§3.3.3): over the last `SLIP_WINDOW_DAYS` local days, each
 * (key, day, kind) once; `other` and unknown kinds never shown; a kind from
 * `RECURRING_SLIP_MIN` occurrences, at most `RECURRING_SLIP_MAX_KINDS`, by
 * count then enum order, each with up to `RECURRING_SLIP_MAX_TOPICS` primary
 * topics (most frequent first, then course order).
 */
export function recurringSlips(
  attempts: readonly EvidenceAttempt[],
  pools: Pick<EvidencePools, "rank">,
  today: string,
): RecurringSlip[] {
  const counter = slipCounter(today);
  for (const a of attempts) counter.add(a);
  return counter.result(pools);
}

/** `recurringSlips` row by row, so `classifyEvidence` counts slips in its own pass. */
function slipCounter(today: string): {
  add: (a: EvidenceAttempt) => void;
  result: (pools: Pick<EvidencePools, "rank">) => RecurringSlip[];
} {
  const from = addDays(today, 1 - SLIP_WINDOW_DAYS);
  const seen = new Set<string>();
  const byKind = new Map<SlipKind, { count: number; topics: Map<string, number> }>();
  const add = (a: EvidenceAttempt): void => {
    if (a.slips.length === 0 || a.day < from || a.day > today) return;
    for (const kind of a.slips) {
      if (kind === "other" || !Object.hasOwn(SLIP_RANK, kind)) continue;
      const slot = `${a.evidenceKey} ${a.day} ${kind}`;
      if (seen.has(slot)) continue;
      seen.add(slot);
      let acc = byKind.get(kind);
      if (!acc) byKind.set(kind, (acc = { count: 0, topics: new Map() }));
      acc.count += 1;
      increment(acc.topics, a.topic);
    }
  };
  const result = (pools: Pick<EvidencePools, "rank">): RecurringSlip[] => {
    const rankOf = (topic: string) => pools.rank.get(topic) ?? Number.MAX_SAFE_INTEGER;
    return [...byKind]
      .filter(([, acc]) => acc.count >= RECURRING_SLIP_MIN)
      .sort(([ka, a], [kb, b]) => b.count - a.count || SLIP_RANK[ka] - SLIP_RANK[kb])
      .slice(0, RECURRING_SLIP_MAX_KINDS)
      .map(([kind, acc]) => ({
        kind,
        count: acc.count,
        topics: [...acc.topics]
          .sort(([ta, na], [tb, nb]) => nb - na || rankOf(ta) - rankOf(tb) || (ta < tb ? -1 : 1))
          .slice(0, RECURRING_SLIP_MAX_TOPICS)
          .map(([topic]) => topic),
      }));
  };
  return { add, result };
}
